import { action, computed, makeObservable, observable } from 'mobx';
import { BiGroup } from 'types/groupService';
import { DashboardEnrollmentPresenter } from 'types/enrollmentService';
import { BiDashboardGroupPresenter, BiGroupInteractionController, GroupAction } from './types';

interface DelegateCreationParams {
  group: BiGroup;
  groupInteractionDelegate: BiGroupInteractionController;
  enrollments?: DashboardEnrollmentPresenter[];
}

export class BiDashboardGroupDelegate {
  private readonly _data: BiDashboardGroupPresenter;
  private readonly _groupInteractionDelegate: BiGroupInteractionController;

  public get data(): BiDashboardGroupPresenter {
    return this._data;
  }

  public archiveGroup(): void {
    this._groupInteractionDelegate.commitGroupAction(
      GroupAction.ARCHIVE,
      this._data.id,
      this._data.name,
      this._data.agency.id,
      this._data.agentOfRecord,
    );
  }

  public moveGroup(): void {
    this._groupInteractionDelegate.commitGroupAction(
      GroupAction.MOVE,
      this._data.id,
      this._data.name,
      this._data.agency.id,
      this._data.agentOfRecord,
    );
  }

  public removeGroup(): void {
    this._groupInteractionDelegate.commitGroupAction(
      GroupAction.REMOVE,
      this._data.id,
      this._data.name,
      this._data.agency.id,
      this._data.agentOfRecord,
    );
  }

  public async unarchiveGroup(): Promise<void> {
    await this._groupInteractionDelegate.unarchiveGroup(this.data.id);
  }

  public async setUserOrgAffiliation(): Promise<void> {
    await this._groupInteractionDelegate.viewGroupProfile(this.data.id, this.data.agency.id, this.data.agency.name);
  }

  constructor({
    group: {
      id,
      name,
      agentOfRecord,
      agency,
      assigned,
      source,
      lastUpdateTime,
      archived,
      carrierGroupNumber,
      contactInfo,
    },
    groupInteractionDelegate,
    enrollments = [],
  }: DelegateCreationParams) {
    if (enrollments && enrollments.length > 0 && enrollments[0].groupId !== id) {
      throw new Error(
        `DashboardGroupDelegate received incompatible group and enrollments! The group id is ${id}, and enrollments' group id is ${enrollments[0].groupId}`,
      );
    }
    this._data = {
      id,
      name,
      agentOfRecord,
      agency,
      assigned,
      source,
      lastUpdateTime,
      enrollments,
      archived,
      carrierGroupNumber,
      contactInfo,
    };
    this._groupInteractionDelegate = groupInteractionDelegate;

    makeObservable<BiDashboardGroupDelegate, '_data'>(this, {
      _data: observable,

      data: computed,
      archiveGroup: action.bound,
      moveGroup: action.bound,
      removeGroup: action.bound,
      setUserOrgAffiliation: action.bound,
    });
  }
}
